import { action, autorun, makeObservable, observable, toJS } from "mobx";
import { message } from "antd";
import {
	CONFIRM_INSURANCE_API_URL,
	PRODUCT_API_URL,
	SAVE_PERSON_API_URL,
} from "../../configs/api-paths";
import BaseInsuranceCalculatorService from "../base/BaseInsuranceCalculatorService";

const LOCAL_STORAGE_NAMESPACE = "form-data";

/**
 * Service dealing with the creation of quotes for clients (Quoting).
 */
class InsuranceQuotingService extends BaseInsuranceCalculatorService {
	/**
	 * Information about the current client returned from server with additional insurer information.
	 * @type {[]}
	 */
	@observable
	insuranceQuotes = [];

	/**
	 * Selected insurance product.
	 * @type {null}
	 */
	@observable
	selectedQuoteToProceed = null;

	/**
	 * Selected insurance product.
	 * @type {String}
	 */
	@observable
	phoneNumberMessage = "";

	constructor(context) {
		super(context);

		autorun(() => {
			if (this.context.applicationService.settings.sessionPersistence) {
				this.context.localStorageService.load(
					LOCAL_STORAGE_NAMESPACE,
					"clientData",
					this.setClientData
				);
			} else {
				this.setClientData({});
			}
		});
		makeObservable(this);
	}

	@action
	setInsuranceQuotes = (data) => {
		this.insuranceQuotes = data;
	};

	@action
	setSelectedQuoteToProceed = (data) => {
		this.selectedQuoteToProceed = data;
	};

	/**
	 * Called when the user changes data. Sets it here and persists to browser storage.
	 * @param key String - name of the field
	 * @param val String - the data
	 */
	changeClientData = (key, val) => {
		super.changeClientData(key, val);

		if (this.context.applicationService.settings.sessionPersistence) {
			this.context.localStorageService.save(
				"clientData",
				toJS(this.clientData)
			);
		}
	};

	setPhoneNumberMessage = (message) => {
		this.phoneNumberMessage = message;
		console.log(message);
	};

	/**
	 * Sends client data to the server to run business logic on it and get insurance data.
	 * @returns {Promise<void>}
	 */
	calculateQuote = async () => {
		this.setInsuranceQuotes([]);
		let hideLoadingMessage = message.loading("Loading...", 0);

		const propsToSend = [
			"organisation",
			"organisation_name",
			"organisation_type",
			"startDate",
			"pl_cover_amt",
			"pl_turnover",
			"el",
			"el_wageroll",
			"el_volunteer_staff",
			"equipment",
			"equipment_cover_amt",
			"uk_only",
			"ngb_qualified",
			"appropriate_training",
			"hazardous_materials",
			"risk_assessment",
			"participation_form",
			"safety_briefing",
			"correct_operation",
			"previous_claims",
			"insurance_declaration",
			"activityTrades",
		];

		try {
			const result = await super.calculateRequest(PRODUCT_API_URL, propsToSend);

			hideLoadingMessage();
			this.setInsuranceQuotes(result);
		} catch (error) {
			console.log(error);
			this.setInsuranceQuotes([]);

			hideLoadingMessage();
			message.error(
				`There was error while trying to send data to the server. Error: ${error}`
			);
		}
	};

	confirmInsurance = async () => {
		try {

			const propsToSend = [
				"quoteId",
				"organisationFirstName",
				"organisationLastName",
				"organisation_name",
				"organisation_type",
				"email",
				"phone",
				"flatNo",
				"address1",
				"address2",
				"address3",
				"address4",
				"address5",
				"postcode",
				"policyStartDate",
			];

			const clientData = this.context.insuranceMtaService.clientData;

			await super.confirmRequest(CONFIRM_INSURANCE_API_URL, propsToSend, {
				quoteId: clientData.quoteId,
				organisationFirstName: clientData.organisation
					?.organisationFirstName,
				organisationLastName: clientData.organisation
					?.organisationLastName,
				organisation_name: clientData.organisation?.organisationName,
				organisation_type: clientData.organisation?.organisationType,
				email: clientData.organisation?.emailAddress,
				phone: clientData?.organisation?.phone,
				flatNo: clientData.organisation?.address.flatNo,
				address1: clientData.organisation?.address?.address1,
				address2: clientData.organisation?.address?.address2,
				address3: clientData.organisation?.address?.address3,
				address4: clientData.organisation?.address.address4,
				address5: clientData.organisation?.address.address5,
				postcode: clientData.organisation?.address?.post_code,
				policyStartDate: clientData.startDate,
			});

			message.success(
				"Successfully confirmed quote. We will send a copy to the email provided."
			);
			return true;
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to send data to the server. Error: ${error}`
			);
			return false;
		}
	};

	savePerson = async () => {
		try {
			const propsToSend = ["email", "post_code"];

			await super.confirmRequest(SAVE_PERSON_API_URL, propsToSend, {
				quoteId: this.selectedQuoteToProceed.quoteId,
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to send data to the server. Error: ${error}`
			);
		}
	};
}

export default InsuranceQuotingService;
